/**
 * useRegister Composable
 * 
 * A composable that handles user registration functionality.
 * It manages the registration form state, handles form submission, and provides error handling.
 * 
 * Features:
 * - Form state management (name, email, password, etc.)
 * - Registration form submission with validation
 * - Error handling and loading states
 * - Password confirmation validation
 * - Automatic redirection after successful registration
 * 
 * @returns {Object} An object containing form state, loading state, error state, and register method
 */

import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/composables/ui/useToast'

export function useRegister() {
  const router = useRouter()
  const authStore = useAuthStore()
  const { success: toastSuccess, error: toastError } = useToast()

  // Form fields
  const firstName = ref('')
  const middleName = ref('')
  const lastName = ref('')
  const email = ref('')
  const phoneNumber = ref('')
  const address = ref('')
  const password = ref('')
  const passwordConfirmation = ref('')

  // State
  const loading = ref(false)
  const error = ref('')
  const success = ref('')

  /**
   * Handle registration form submission
   */
  const handleRegister = async (): Promise<boolean> => {
    try {
      loading.value = true
      error.value = ''
      success.value = ''

      // Validate required fields
      if (!firstName.value || !lastName.value || !email.value || !password.value || !passwordConfirmation.value) {
        throw new Error('Please fill in all required fields')
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email.value)) {
        throw new Error('Please enter a valid email address')
      }

      // Validate password length
      if (password.value.length < 8) {
        throw new Error('Password must be at least 8 characters long')
      }

      // Validate password confirmation
      if (password.value !== passwordConfirmation.value) {
        throw new Error('Passwords do not match')
      }

      // Prepare registration data
      const registrationData = {
        first_name: firstName.value,
        middle_name: middleName.value || null,
        last_name: lastName.value,
        email: email.value,
        phone_number: phoneNumber.value || null,
        address: address.value || null,
        password: password.value,
        password_confirmation: passwordConfirmation.value
      }

      // Call the registration API
      await authStore.register(registrationData)
      
      // Clear form
      firstName.value = ''
      middleName.value = ''
      lastName.value = ''
      email.value = ''
      phoneNumber.value = ''
      address.value = ''
      password.value = ''
      passwordConfirmation.value = ''

      return true

    } catch (err: any) {
      // Extract validation errors if available
      let errorMessage = 'An error occurred during registration'
      if (err.response?.data?.errors) {
        errorMessage = Object.values(err.response.data.errors).flat().join(', ')
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err instanceof Error) {
        errorMessage = err.message
      }
      
      error.value = errorMessage
      toastError(errorMessage, 6000)
      return false
    } finally {
      loading.value = false
    }
  }

  return {
    firstName,
    middleName,
    lastName,
    email,
    phoneNumber,
    address,
    password,
    passwordConfirmation,
    loading,
    error,
    success,
    handleRegister
  }
}
